import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FileDropZone } from '@/lib/base/file-upload';
import { ProgressBar } from '@/lib/base/progress-indicators/progress-indicators';
import { fileService, type UploadedFile } from '@/lib/files/service';
import { logger } from '@/lib/logger';

interface FileUploadProps {
  orgId: number;
  onFileUploaded?: (file: UploadedFile) => void | Promise<void>;
  onError?: (error: string) => void;
}

export type { UploadedFile };

export const FileUpload: React.FC<FileUploadProps> = ({
  orgId,
  onFileUploaded,
  onError,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const isUploadingRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      // Guard against concurrent uploads
      if (isUploadingRef.current) {
        return;
      }

      isUploadingRef.current = true;
      setIsUploading(true);
      setUploadProgress(0);

      try {
        const uploadedFile = await fileService.uploadFile(orgId, file, {
          onProgress: setUploadProgress,
        });
        await onFileUploaded?.(uploadedFile);
        setUploadProgress(100);
      } catch (error) {
        logger.error(
          'File upload failed',
          error instanceof Error ? error : undefined,
          { component: 'FileUpload' }
        );
        onError?.("We couldn't upload this file. Try again.");
      } finally {
        isUploadingRef.current = false;
        if (isMountedRef.current) {
          setIsUploading(false);
          setUploadProgress(0);
        }
      }
    },
    [orgId, onFileUploaded, onError]
  );

  // FileDropZone supports multi-select; this surface is single-file (v1), so
  // we take the first file from the selection.
  const handleFilesSelected = useCallback(
    (files: FileList) => {
      if (files.length > 0) {
        uploadFile(files[0]);
      }
    },
    [uploadFile]
  );

  if (isUploading) {
    return (
      <div className='rounded-lg border-2 border-dashed border-[var(--color-border-secondary)] px-6 py-8'>
        <div className='flex flex-col gap-2'>
          <span className='text-sm font-medium text-[var(--color-text-secondary)]'>
            Uploading…
          </span>
          <ProgressBar value={uploadProgress} labelPosition='right' />
        </div>
      </div>
    );
  }

  return (
    <FileDropZone onFilesSelected={handleFilesSelected} multiple={false} />
  );
};
